// Admin · Regions
// Manage the 3 "Xaluca Across Morocco" regions: trilingual name +
// description, color, center coordinates, highlight radius, property
// assignments and display order.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Save, RefreshCw, Loader2, CheckCircle2, AlertCircle, Check,
} from "lucide-react";
import AdminAuthGate, { adminHeaders } from "@/components/admin/AdminAuthGate";
import { HOTELS } from "@/lib/data";

const API = process.env.REACT_APP_BACKEND_URL || "";
const LANGS = [
  { id: "en", label: "EN" },
  { id: "fr", label: "FR" },
  { id: "es", label: "ES" },
];
const blankI18n = () => ({ en: "", fr: "", es: "" });

const safeReadBody = async (res) => {
  try { return await res.json(); } catch { return null; }
};

const I18nTextarea = ({ label, value, onChange, rows = 4, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">{label}</p>
    <div className="space-y-2">
      {LANGS.map((l) => (
        <label key={l.id} className="flex gap-2 items-start">
          <span className="mt-2.5 inline-block w-7 text-[10px] tracking-[0.2em] uppercase text-[#C16542] font-medium">
            {l.label}
          </span>
          <textarea
            value={value?.[l.id] || ""}
            onChange={(e) => onChange({ ...(value || blankI18n()), [l.id]: e.target.value })}
            rows={rows}
            className="flex-1 border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] bg-white"
            data-testid={`${testid}-${l.id}`}
          />
        </label>
      ))}
    </div>
  </div>
);

const RegionEditor = ({ slug, onSaved }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/regions/${slug}`, {
      credentials: "include", headers: adminHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const togglePropertySlug = (propSlug) => {
    const cur = data.property_slugs || [];
    const next = cur.includes(propSlug)
      ? cur.filter((s) => s !== propSlug)
      : [...cur, propSlug];
    update({ property_slugs: next });
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/regions/${slug}`, {
      credentials: "include",
        method: "PUT",
        headers: { ...adminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          fields: {
            name: data.name,
            description: data.description,
            color: data.color,
            center: data.center,
            radius_km: data.radius_km,
            property_slugs: data.property_slugs || [],
            order: data.order,
          },
        }),
      });
      if (!res.ok) {
        const body = await safeReadBody(res);
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      setData(await res.json());
      setSavedAt(new Date());
      onSaved && onSaved(slug);
    } catch (e) {
      setError(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset this region to its default values? Any custom overrides will be lost.")) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/regions/${slug}`, {
      credentials: "include",
        method: "DELETE",
        headers: adminHeaders(),
      });
      if (!res.ok) {
        const body = await safeReadBody(res);
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      await load();
      setSavedAt(new Date());
      onSaved && onSaved(slug);
    } catch (e) {
      setError(e.message || "Failed to reset");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-3 text-[#5C5248]" data-testid="admin-regions-editor-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading…
      </div>
    );
  }
  if (!data) return <p className="p-8 text-sm text-[#C16542]">{error || "No data"}</p>;

  const isOverride = !!data.is_override;
  const selectedSet = new Set(data.property_slugs || []);

  return (
    <div className="space-y-8" data-testid="admin-regions-editor">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline text-[#C16542]">Region</p>
          <h2 className="font-serif-x text-3xl md:text-4xl tracking-tight mt-1">
            {data.name?.en || slug}
          </h2>
          <p className="mt-1 text-xs text-[#5C5248]">
            Slug: <span className="tabular-nums">{slug}</span>
            {isOverride ? " · Custom CMS override active" : " · Using static defaults"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {savedAt ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#5C7C4F]">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Saved {savedAt.toLocaleTimeString()}
            </span>
          ) : null}
          {isOverride ? (
            <button
              type="button"
              onClick={reset}
              disabled={saving}
              className="inline-flex items-center gap-2 border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors disabled:opacity-50"
              data-testid="admin-regions-reset"
            >
              <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.6} />
              Reset to defaults
            </button>
          ) : null}
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-50 text-[#FDFBF7] px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
            data-testid="admin-regions-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" strokeWidth={1.8} />}
            Save
          </button>
        </div>
      </header>

      {error ? (
        <div className="border border-[#C16542]/40 bg-[#C16542]/5 px-4 py-3 text-sm text-[#C16542] flex items-center gap-2" data-testid="admin-regions-error">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      ) : null}

      {/* Name (i18n) */}
      <section className="bg-white border border-[#2C2621]/10 p-5 md:p-6">
        <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">Display name</p>
        <div className="space-y-2">
          {LANGS.map((l) => (
            <label key={l.id} className="flex gap-2 items-center">
              <span className="inline-block w-7 text-[10px] tracking-[0.2em] uppercase text-[#C16542] font-medium">
                {l.label}
              </span>
              <input
                type="text"
                value={data.name?.[l.id] || ""}
                onChange={(e) => update({ name: { ...(data.name || blankI18n()), [l.id]: e.target.value } })}
                className="flex-1 border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542]"
                data-testid={`admin-regions-name-${l.id}`}
              />
            </label>
          ))}
        </div>
      </section>

      {/* Description (i18n) */}
      <section className="bg-white border border-[#2C2621]/10 p-5 md:p-6">
        <I18nTextarea
          label="Description"
          rows={3}
          value={data.description}
          onChange={(v) => update({ description: v })}
          testid="admin-regions-description"
        />
      </section>

      {/* Visual */}
      <section className="bg-white border border-[#2C2621]/10 p-5 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">Marker / overlay color</span>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={data.color || "#C16542"}
              onChange={(e) => update({ color: e.target.value })}
              className="w-12 h-10 border border-[#2C2621]/15 cursor-pointer"
              data-testid="admin-regions-color"
            />
            <input
              type="text"
              value={data.color || ""}
              onChange={(e) => update({ color: e.target.value })}
              placeholder="#C16542"
              className="flex-1 border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] font-mono"
            />
          </div>
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2">Display order</span>
          <input
            type="number"
            min={1}
            max={50}
            value={data.order ?? 1}
            onChange={(e) => update({ order: parseInt(e.target.value, 10) || 1 })}
            className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] tabular-nums"
            data-testid="admin-regions-order"
          />
        </label>
      </section>

      {/* Coordinates + radius */}
      <section className="bg-white border border-[#2C2621]/10 p-5 md:p-6">
        <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-3">Center & highlight radius</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block">
            <span className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">Latitude</span>
            <input
              type="number"
              step="0.0001"
              value={data.center?.[0] ?? ""}
              onChange={(e) =>
                update({ center: [parseFloat(e.target.value) || 0, data.center?.[1] ?? 0] })
              }
              className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] tabular-nums"
              data-testid="admin-regions-lat"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">Longitude</span>
            <input
              type="number"
              step="0.0001"
              value={data.center?.[1] ?? ""}
              onChange={(e) =>
                update({ center: [data.center?.[0] ?? 0, parseFloat(e.target.value) || 0] })
              }
              className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] tabular-nums"
              data-testid="admin-regions-lng"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1">Radius (km)</span>
            <input
              type="number"
              min={10}
              max={400}
              step={5}
              value={data.radius_km ?? 80}
              onChange={(e) => update({ radius_km: parseFloat(e.target.value) || 80 })}
              className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] tabular-nums"
              data-testid="admin-regions-radius"
            />
          </label>
        </div>
      </section>

      {/* Property assignments */}
      <section className="bg-white border border-[#2C2621]/10 p-5 md:p-6">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
            Properties in this region
          </p>
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#C16542]" data-testid="admin-regions-prop-count">
            {selectedSet.size} selected
          </span>
        </div>
        <ul className="grid sm:grid-cols-2 gap-2" data-testid="admin-regions-property-grid">
          {HOTELS.map((h) => {
            const checked = selectedSet.has(h.slug);
            return (
              <li key={h.slug}>
                <button
                  type="button"
                  onClick={() => togglePropertySlug(h.slug)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 border text-left transition-colors ${
                    checked
                      ? "border-[#C16542] bg-[#C16542]/5 text-[#1A1513]"
                      : "border-[#2C2621]/10 hover:border-[#C16542]/60 text-[#2C2621]"
                  }`}
                  data-testid={`admin-regions-prop-${h.slug}`}
                >
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium truncate">{h.name}</span>
                    <span className="block text-[10px] tracking-[0.2em] uppercase text-[#5C5248] mt-0.5">
                      {h.category} · {h.location}
                    </span>
                  </span>
                  <span
                    className={`inline-flex items-center justify-center w-5 h-5 border ${
                      checked ? "bg-[#C16542] border-[#C16542] text-white" : "border-[#2C2621]/30"
                    }`}
                  >
                    {checked ? <Check className="w-3 h-3" strokeWidth={2.5} /> : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
};

const AdminRegionsInner = () => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/regions`, {
      credentials: "include", headers: adminHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = (data?.items || []).sort((a, b) => (a.order || 99) - (b.order || 99));
      setItems(list);
      if (!selected && list.length > 0) setSelected(list[0].slug);
    } catch (e) {
      setError(e.message || "Failed to load regions");
    }
  }, [selected]);

  useEffect(() => { load(); }, [load]);

  const overrideMap = useMemo(
    () => Object.fromEntries(items.map((r) => [r.slug, !!r.is_override])),
    [items]
  );

  return (
    <section className="bg-[#FDFBF7] min-h-screen" data-testid="admin-regions-page">
      <header className="px-6 md:px-10 py-8 border-b border-[#2C2621]/10 bg-white">
        <div className="max-w-[1400px] mx-auto flex flex-wrap items-end justify-between gap-4">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542] mb-3"
              data-testid="admin-regions-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to admin
            </Link>
            <p className="overline text-[#C16542]">Grup Xaluca · Admin · Regions</p>
            <h1 className="font-serif-x text-4xl md:text-5xl tracking-tight mt-1">
              Xaluca Across Morocco
            </h1>
            <p className="mt-2 text-sm text-[#5C5248] max-w-2xl">
              Manage the regional groupings displayed on the public /directions map: names, descriptions, colors, GPS centers, highlight radius and property assignments — in English, French and Spanish.
            </p>
          </div>
        </div>
      </header>

      {error ? (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-6">
          <p className="text-sm text-[#C16542]">{error}</p>
        </div>
      ) : null}

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-28 self-start" data-testid="admin-regions-sidebar">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-3">Regions</p>
          <ul className="space-y-1">
            {items.map((r) => {
              const active = r.slug === selected;
              return (
                <li key={r.slug}>
                  <button
                    type="button"
                    onClick={() => setSelected(r.slug)}
                    className={`w-full text-left px-3 py-2.5 text-sm flex items-center justify-between gap-2 transition-colors ${
                      active ? "bg-[#2C2621] text-[#FDFBF7]" : "hover:bg-[#F2EBE1] text-[#2C2621]"
                    }`}
                    data-testid={`admin-regions-pick-${r.slug}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: r.color }}
                      />
                      <span className="truncate">{r.name?.en || r.slug}</span>
                    </span>
                    {overrideMap[r.slug] ? (
                      <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-[#D4A373]" : "bg-[#C16542]"}`} title="Has custom override" />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Editor */}
        <main>
          {selected ? (
            <RegionEditor key={selected} slug={selected} onSaved={load} />
          ) : (
            <p className="text-sm text-[#5C5248]">Pick a region on the left.</p>
          )}
        </main>
      </div>
    </section>
  );
};

const AdminRegionsPage = () => (
  <AdminAuthGate>
    <AdminRegionsInner />
  </AdminAuthGate>
);

export default AdminRegionsPage;
